import React, { useState, useEffect, Component, lazy, Suspense } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { HarmonyLogoCompact } from '../components/HarmonyLogo';
import { PulseProvider, usePulse } from '../context/PulseContext';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Button } from '../components/ui/button';
import { Avatar, AvatarFallback } from '../components/ui/avatar';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Activity, ListChecks, Radio, Laptop, Settings, UserCog, ScrollText,
  Scale, ScanSearch, BookOpen, ChevronDown, LogOut, AlertTriangle, RefreshCw, ShieldCheck,
  CalendarClock, CalendarDays, CalendarRange, HeartPulse, Globe,
} from 'lucide-react';
import usePageMeta from '../hooks/usePageMeta';

// Code-split each tab so the initial bundle is just the shell.
const PulseReports = lazy(() => import('../components/pulse/PulseReports'));
const PulseReconcile = lazy(() => import('../components/pulse/PulseReconcile'));
const PulseAnomalies = lazy(() => import('../components/pulse/PulseAnomalies'));
const PulseLive = lazy(() => import('../components/pulse/PulseLive'));
const PulseDevices = lazy(() => import('../components/pulse/PulseDevices'));
const PulseGuide = lazy(() => import('../components/pulse/PulseGuide'));
const PulseAccess = lazy(() => import('../components/pulse/PulseAccess'));
const PulseAudit = lazy(() => import('../components/pulse/PulseAudit'));
const PulseSettings = lazy(() => import('../components/pulse/PulseSettings'));
const PulseShifts = lazy(() => import('../components/pulse/PulseShifts'));
const PulseSummary = lazy(() => import('../components/pulse/PulseSummary'));
const PulseWeekly = lazy(() => import('../components/pulse/PulseWeekly'));
const PulseHealth = lazy(() => import('../components/pulse/PulseHealth'));
const PulseBrowsing = lazy(() => import('../components/pulse/PulseBrowsing'));

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

class PulseErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null };
  }
  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }
  componentDidCatch(error, info) {
    // eslint-disable-next-line no-console
    console.error('Harmony Pulse crashed:', error, info);
  }
  render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center max-w-md space-y-3">
            <AlertTriangle className="w-10 h-10 text-destructive mx-auto" />
            <h1 className="text-xl font-semibold">Something went wrong</h1>
            <p className="text-sm text-muted-foreground">
              {this.state.error?.message || 'An unexpected error occurred.'}
            </p>
            <Button onClick={() => window.location.reload()}>Reload</Button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

// group:'admin' tabs are collapsed behind a single "Admin" nav button and shown
// as a second-level bar once you are inside the group — the day-to-day tabs stay
// on the top row instead of competing with five setup screens.
const TABS = [
  // Live first: it is the page people open by default, and the first tab is
  // also the fallback when a URL names a tab the viewer cannot see.
  { key: 'live',     label: 'Live',     icon: Radio,      adminOnly: false },
  { key: 'summary',  label: 'Summary',  icon: CalendarDays, adminOnly: false },
  { key: 'weekly',   label: 'Weekly',   icon: CalendarRange, adminOnly: false },
  { key: 'reports',  label: 'Reports',  icon: ListChecks, adminOnly: false },
  { key: 'reconcile', label: 'Reconcile', icon: Scale,    adminOnly: false },
  { key: 'anomalies', label: 'Anomalies', icon: ScanSearch, adminOnly: false },
  { key: 'devices',  label: 'Devices',  icon: Laptop,     adminOnly: true, group: 'admin' },
  { key: 'setup',    label: 'Setup',    icon: BookOpen,   adminOnly: true, group: 'admin' },
  { key: 'health',   label: 'Health',   icon: HeartPulse, adminOnly: true, group: 'admin' },
  { key: 'browsing', label: 'Browsing', icon: Globe,      adminOnly: true, group: 'admin' },
  { key: 'shifts',   label: 'Shifts',   icon: CalendarClock, adminOnly: true, group: 'admin' },
  { key: 'access',   label: 'Access',   icon: UserCog,    adminOnly: true, group: 'admin' },
  { key: 'audit',    label: 'Audit',    icon: ScrollText, adminOnly: true, group: 'admin' },
  { key: 'settings', label: 'Settings', icon: Settings,   adminOnly: true, group: 'admin' },
];

const PulseContent = () => {
  usePageMeta('Harmony Pulse | Harmony Apps', '/favicon-pulse.svg');

  const [searchParams, setSearchParams] = useSearchParams();
  const { userName, role, isAdmin, bootError } = usePulse();

  const tabFromUrl = searchParams.get('tab') || 'live';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  useEffect(() => {
    if (tabFromUrl !== activeTab) setActiveTab(tabFromUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tabFromUrl]);

  const handleTabChange = (next) => {
    setActiveTab(next);
    const params = new URLSearchParams(searchParams);
    params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const getInitials = (name) =>
    (name || 'U').split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2);

  if (isAdmin === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
          <p className="text-sm">Loading Harmony Pulse…</p>
        </div>
      </div>
    );
  }

  if (bootError === 'forbidden') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background p-6">
        <div className="text-center max-w-md space-y-3">
          <ShieldCheck className="w-10 h-10 text-muted-foreground mx-auto" />
          <h1 className="text-xl font-semibold">No access</h1>
          <p className="text-sm text-muted-foreground">
            Harmony Pulse is available to HR, management and team leads only.
            If you believe you should have access, contact HR.
          </p>
        </div>
      </div>
    );
  }

  const visibleTabs = TABS.filter((t) => !t.adminOnly || isAdmin);
  const safeTab = visibleTabs.some((t) => t.key === activeTab)
    ? activeTab
    : (visibleTabs[0]?.key || 'reports');
  const mainTabs = visibleTabs.filter((t) => !t.group);
  const adminTabs = visibleTabs.filter((t) => t.group === 'admin');
  const adminActive = adminTabs.some((t) => t.key === safeTab);

  return (
    <div className="min-h-screen bg-background" data-testid="pulse-app">
      <nav className="sticky top-0 z-50 bg-white border-b border-border shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-3">
              <HarmonyLogoCompact size={32} />
              <div>
                <h1 className="font-semibold text-lg text-foreground flex items-center gap-2">
                  <Activity className="w-5 h-5 text-indigo-600" />
                  Harmony Pulse
                </h1>
                <p className="text-xs text-muted-foreground">Harmony Apps</p>
              </div>
            </div>

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="pulse-user-menu">
                  <Avatar className="h-8 w-8">
                    <AvatarFallback className="bg-indigo-600 text-white text-sm">
                      {getInitials(userName)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm font-medium">{userName}</span>
                  <ChevronDown className="w-4 h-4 text-muted-foreground" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>
                  <p className="text-sm font-medium">{userName}</p>
                  <p className="text-xs text-muted-foreground capitalize">
                    {(role || '').replace('_', ' ')}
                  </p>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => window.close()} className="text-destructive">
                  <LogOut className="mr-2 h-4 w-4" />
                  Close App
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          <div className="pb-2 -mx-4 px-4 overflow-x-auto">
            <Tabs value={safeTab} onValueChange={handleTabChange}>
              <TabsList>
                {mainTabs.map((t) => {
                  const Icon = t.icon;
                  return (
                    <TabsTrigger key={t.key} value={t.key} data-testid={`pulse-tab-${t.key}`}>
                      <Icon className="w-4 h-4 mr-2" />
                      {t.label}
                    </TabsTrigger>
                  );
                })}
                {adminTabs.length > 0 && (
                  // Its value is the ACTIVE admin tab while we're inside the group, so the
                  // trigger highlights; otherwise it points at the group's landing tab.
                  <TabsTrigger
                    value={adminActive ? safeTab : adminTabs[0].key}
                    data-testid="pulse-tab-admin"
                  >
                    <ShieldCheck className="w-4 h-4 mr-2" />
                    Admin
                    <ChevronDown className="w-3.5 h-3.5 ml-1 opacity-60" />
                  </TabsTrigger>
                )}
              </TabsList>
            </Tabs>

            {adminActive && (
              <div className="mt-2">
                <Tabs value={safeTab} onValueChange={handleTabChange}>
                  <TabsList className="h-8 bg-slate-100">
                    {adminTabs.map((t) => {
                      const Icon = t.icon;
                      return (
                        <TabsTrigger
                          key={t.key} value={t.key} data-testid={`pulse-tab-${t.key}`}
                          className="text-xs py-1"
                        >
                          <Icon className="w-3.5 h-3.5 mr-1.5" />
                          {t.label}
                        </TabsTrigger>
                      );
                    })}
                  </TabsList>
                </Tabs>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <Suspense
          fallback={
            <div className="flex items-center justify-center py-16 text-sm text-muted-foreground">
              <RefreshCw className="w-4 h-4 animate-spin mr-2" />
              Loading…
            </div>
          }
        >
          {safeTab === 'summary' && <PulseSummary />}
          {safeTab === 'weekly' && <PulseWeekly />}
          {safeTab === 'reports' && <PulseReports />}
          {safeTab === 'reconcile' && <PulseReconcile />}
          {safeTab === 'anomalies' && <PulseAnomalies />}
          {safeTab === 'live' && <PulseLive />}
          {safeTab === 'devices' && isAdmin && <PulseDevices />}
          {safeTab === 'setup' && isAdmin && <PulseGuide />}
          {safeTab === 'health' && isAdmin && <PulseHealth />}
          {safeTab === 'browsing' && isAdmin && <PulseBrowsing />}
          {safeTab === 'shifts' && isAdmin && <PulseShifts />}
          {safeTab === 'access' && isAdmin && <PulseAccess />}
          {safeTab === 'audit' && isAdmin && <PulseAudit />}
          {safeTab === 'settings' && isAdmin && <PulseSettings />}
        </Suspense>
      </main>
    </div>
  );
};

// Best-effort JWT payload decode — fallback only, used when /auth/me errors.
const _peekJwt = (jwt) => {
  try {
    const [, payload] = (jwt || '').split('.');
    if (!payload) return null;
    const base = payload.replace(/-/g, '+').replace(/_/g, '/');
    return JSON.parse(atob(base + '='.repeat((4 - base.length % 4) % 4)));
  } catch (_) { return null; }
};

const PulseApp = () => {
  // Cookie bootstrap: no token in the URL. The HttpOnly refresh cookie set at
  // shell login rides along on a same-origin request, and we trade it for a
  // short-lived access token held in memory only.
  const [bootState, setBootState] = useState({ status: 'loading' });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const refresh = await axios.post(
          `${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true },
        );
        const token = refresh.data?.token;
        if (!token) throw new Error('No token in refresh response');

        let userName = 'User';
        let role = 'viewer';
        let userId = null;
        try {
          const me = await axios.get(`${BACKEND_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          userName = me.data?.full_name || me.data?.username || userName;
          role = me.data?.role || role;
          userId = me.data?.id || null;
        } catch (_) {
          const claims = _peekJwt(token);
          if (claims?.username) userName = claims.username;
          if (claims?.role) role = claims.role;
          if (claims?.sub) userId = claims.sub;
        }

        if (!cancelled) setBootState({ status: 'ready', token, userName, role, userId });
      } catch (e) {
        if (!cancelled) setBootState({ status: 'unauthenticated' });
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (bootState.status === 'loading') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
          <p className="text-sm">Signing in…</p>
        </div>
      </div>
    );
  }

  if (bootState.status === 'unauthenticated') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-bold text-destructive mb-2">Session expired</h1>
          <p className="text-muted-foreground mb-4">
            Please sign in to the Harmony Apps dashboard, then launch Harmony Pulse again.
          </p>
          <Button onClick={() => { window.location.href = `${window.location.origin}/login`; }}>
            Go to sign-in
          </Button>
        </div>
      </div>
    );
  }

  return (
    <PulseErrorBoundary>
      <PulseProvider
        token={bootState.token}
        role={bootState.role}
        userName={bootState.userName}
        userId={bootState.userId}
      >
        <PulseContent />
      </PulseProvider>
    </PulseErrorBoundary>
  );
};

export default PulseApp;
